/**
 * The menu's ambient bed — one generated loop, mounted with the shell, gone with it.
 *
 * Rules, each one a policy rather than a preference:
 *   - **Starts on the first user gesture**, never on load: the autoplay policy would refuse it
 *     anyway, and a menu that begins making sound before the player has touched anything is
 *     the wrong first impression. One `pointerdown`/`keydown` listener arms it, then removes
 *     itself.
 *   - **`musicEnabled` is the hard switch, `masterVolume` scales it.** Both live in the
 *     player's real settings (`engine/settings.ts`); the Settings screen's toggle takes effect
 *     LIVE through the `bl:settings-changed` event `saveSettings` now emits — no reload, no
 *     route round-trip.
 *   - **Hidden tab = silence.** The loop pauses on `visibilitychange` and resumes with the tab;
 *     menu music from a background tab is how a player learns to mute the whole site.
 *   - The arena never hears this: the element lives and dies with the shell, and entering a
 *     match unmounts the shell.
 *   - An environment without `Audio` (jsdom) or a missing file mounts a silent no-op.
 */
import { loadSettings, type PlayerSettings } from '../settings';

const TRACK_URL = '/game/audio/ui/menu-ambient.mp3';
/** The bed sits far under the SFX — present, never listened *to*. */
const BASE_GAIN = 0.28;

export interface MenuMusicHandle {
  dispose(): void;
}

export function mountMenuMusic(): MenuMusicHandle {
  if (typeof Audio !== 'function') {
    return { dispose() {} };
  }

  const el = new Audio(TRACK_URL);
  el.loop = true;
  el.preload = 'none';

  let armed = false;
  let disposed = false;

  function applySettings(settings: PlayerSettings = loadSettings()): void {
    el.volume = Math.min(1, Math.max(0, BASE_GAIN * settings.masterVolume));
    if (!settings.musicEnabled || settings.masterVolume <= 0) {
      el.pause();
    } else if (armed && document.visibilityState !== 'hidden') {
      void el.play().catch(() => {
        /* not yet allowed, or the file is missing — stay silent */
      });
    }
  }

  function onFirstGesture(): void {
    if (disposed || armed) return;
    armed = true;
    window.removeEventListener('pointerdown', onFirstGesture);
    window.removeEventListener('keydown', onFirstGesture);
    applySettings();
  }

  function onVisibility(): void {
    if (document.visibilityState === 'hidden') el.pause();
    else applySettings();
  }

  function onSettingsChanged(event: Event): void {
    const detail = (event as CustomEvent<PlayerSettings>).detail;
    applySettings(detail ?? loadSettings());
  }

  window.addEventListener('pointerdown', onFirstGesture);
  window.addEventListener('keydown', onFirstGesture);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('bl:settings-changed', onSettingsChanged);

  return {
    dispose(): void {
      disposed = true;
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('bl:settings-changed', onSettingsChanged);
      el.pause();
      el.src = '';
    },
  };
}
